import { useEffect, useId, useState, type CSSProperties } from 'react';
import {
  APPEARANCE_PRESETS,
  DEFAULT_APPEARANCE_COLOR,
  normalizeHexColor,
} from './appearance-colors';
import { Button, ErrorState, Field } from './ui';

export interface AppearanceSettingsProps {
  color: string;
  savedColor: string;
  onPreview: (color: string) => void;
  onSave: (color: string) => void;
  onCancel: () => void;
  saving: boolean;
  error: Error | null;
  disabled?: boolean;
}

const CHANNELS = [
  { key: 'red', label: 'Красный', shortLabel: 'R' },
  { key: 'green', label: 'Зелёный', shortLabel: 'G' },
  { key: 'blue', label: 'Синий', shortLabel: 'B' },
] as const;

function rgbChannels(color: string): [number, number, number] {
  return [
    Number.parseInt(color.slice(1, 3), 16),
    Number.parseInt(color.slice(3, 5), 16),
    Number.parseInt(color.slice(5, 7), 16),
  ];
}

function rgbHex(channels: number[]): string {
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function AppearanceSettings({
  color,
  savedColor,
  onPreview,
  onSave,
  onCancel,
  saving,
  error,
  disabled = false,
}: AppearanceSettingsProps) {
  const id = useId();
  const previewColor = normalizeHexColor(color) || DEFAULT_APPEARANCE_COLOR;
  const originalColor = normalizeHexColor(savedColor) || DEFAULT_APPEARANCE_COLOR;
  const [hexDraft, setHexDraft] = useState<string>(previewColor);
  useEffect(() => {
    setHexDraft(normalizeHexColor(color) || DEFAULT_APPEARANCE_COLOR);
  }, [color]);

  const draftColor = normalizeHexColor(hexDraft);
  const changed = draftColor !== null && draftColor !== originalColor;
  const hasDraft = hexDraft !== originalColor || previewColor !== originalColor;
  const controlsDisabled = saving || disabled;
  const channels = rgbChannels(previewColor);
  const validation = draftColor
    ? null
    : /^#?[\da-f]{0,5}$/i.test(hexDraft.trim())
      ? `Укажите все шесть цифр цвета, например ${DEFAULT_APPEARANCE_COLOR}.`
      : 'Используйте формат #RRGGBB: цифры 0–9 и буквы A–F.';

  const preview = (nextColor: string) => {
    const normalized = normalizeHexColor(nextColor);
    if (!normalized) return;
    setHexDraft(normalized);
    onPreview(normalized);
  };

  return (
    <form
      className="appearance-settings"
      aria-label="Настройки фона"
      aria-busy={saving}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (draftColor && changed && !controlsDisabled) onSave(draftColor);
      }}
    >
      <fieldset className="appearance-presets" disabled={controlsDisabled}>
        <legend>Фон</legend>
        <div className="appearance-preset-grid">
          {APPEARANCE_PRESETS.map((preset) => {
            const selected = previewColor === normalizeHexColor(preset.color);
            return (
              <button
                key={preset.id}
                type="button"
                className={`appearance-preset${selected ? ' selected' : ''}`}
                aria-label={preset.name}
                aria-pressed={selected}
                title={preset.name}
                onClick={() => preview(preset.color)}
              >
                <span
                  className="appearance-preset-swatch"
                  style={{ background: preset.swatchBackground }}
                  aria-hidden="true"
                />
                <span className="appearance-preset-name" aria-hidden="true">
                  {preset.name}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <section className="appearance-custom" aria-labelledby={`${id}-custom-heading`}>
        <div className="appearance-custom-heading">
          <h4 id={`${id}-custom-heading`}>Свой цвет</h4>
          <Button
            className="appearance-default-reset"
            disabled={
              controlsDisabled || (previewColor === DEFAULT_APPEARANCE_COLOR && !validation)
            }
            onClick={() => preview(DEFAULT_APPEARANCE_COLOR)}
          >
            По умолчанию
          </Button>
        </div>
        <div className="appearance-hex-field">
          <Field
            label="HEX-код"
            id={`${id}-hex`}
            type="text"
            value={hexDraft}
            placeholder={DEFAULT_APPEARANCE_COLOR}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={controlsDisabled}
            aria-invalid={Boolean(validation)}
            aria-describedby={`${id}-hex-help`}
            onChange={(event) => {
              const value = event.target.value;
              setHexDraft(value);
              const normalized = normalizeHexColor(value);
              if (normalized) onPreview(normalized);
            }}
          />
          <p
            id={`${id}-hex-help`}
            className={validation ? 'appearance-validation' : 'muted'}
            role={validation ? 'status' : undefined}
          >
            {validation ||
              'Введите цвет или настройте его ползунками RGB. Предпросмотр меняется сразу.'}
          </p>
        </div>
        <div className="appearance-rgb" role="group" aria-label="Настройка цвета RGB">
          {CHANNELS.map((channel, index) => {
            const low = [...channels];
            const high = [...channels];
            low[index] = 0;
            high[index] = 255;
            const style = {
              '--appearance-channel-gradient': `linear-gradient(to right, ${rgbHex(low)}, ${rgbHex(high)})`,
            } as CSSProperties;
            return (
              <div className="appearance-channel" key={channel.key}>
                <div className="appearance-channel-heading">
                  <label htmlFor={`${id}-${channel.key}`}>
                    <span aria-hidden="true">{channel.shortLabel}</span> {channel.label}
                  </label>
                  <output htmlFor={`${id}-${channel.key}`}>{channels[index]}</output>
                </div>
                <input
                  className="appearance-channel-slider"
                  id={`${id}-${channel.key}`}
                  type="range"
                  min={0}
                  max={255}
                  step={1}
                  value={channels[index]}
                  disabled={controlsDisabled}
                  style={style}
                  onChange={(event) => {
                    const next = [...channels];
                    next[index] = Number(event.target.value);
                    preview(rgbHex(next));
                  }}
                />
              </div>
            );
          })}
        </div>
      </section>

      {error && <ErrorState error={error} />}
      <div className="appearance-actions">
        <span className="muted appearance-status" role="status">
          {disabled
            ? 'Изменение цвета недоступно.'
            : saving
              ? 'Сохраняем выбранный цвет…'
              : validation
                ? 'Дополните HEX-код или выберите готовый оттенок.'
                : changed
                  ? 'Предпросмотр активен. Сохраните выбранный цвет.'
                  : 'Цвет сохранён.'}
        </span>
        <Button
          disabled={controlsDisabled || !hasDraft}
          onClick={() => {
            setHexDraft(originalColor);
            onPreview(originalColor);
            onCancel();
          }}
        >
          Отмена
        </Button>
        <Button
          className="primary"
          type="submit"
          disabled={controlsDisabled || !draftColor || !changed}
        >
          {saving ? 'Сохранение…' : 'Сохранить цвет'}
        </Button>
      </div>
    </form>
  );
}
